import { getDB } from "./schema";
import type { TileData, ViewData } from "../types";

// ─── Panel Layouts ──────────────────────────────────────

export async function saveLayout(record: {
  runId: string;
  panelWidth: number;
  isOpen: boolean;
  tileLayouts?: Record<string, { collapsed: boolean; height: number | null }>;
  activeViewId?: string;
}): Promise<void> {
  const db = await getDB();
  // Merge with existing record to avoid clobbering tileLayouts when only saving panel state
  const existing = await db.get("panelLayouts", record.runId);
  await db.put("panelLayouts", {
    ...existing,
    ...record,
    updatedAt: new Date().toISOString(),
  });
}

export async function saveTileLayouts(
  runId: string,
  tileLayouts: Record<string, { collapsed: boolean; height: number | null }>,
): Promise<void> {
  const db = await getDB();
  const existing = await db.get("panelLayouts", runId);
  await db.put("panelLayouts", {
    runId,
    panelWidth: existing?.panelWidth ?? 35,
    isOpen: existing?.isOpen ?? false,
    tileLayouts,
    updatedAt: new Date().toISOString(),
  });
}

export async function getLayout(runId: string) {
  const db = await getDB();
  return db.get("panelLayouts", runId) ?? null;
}

export async function deleteLayout(runId: string): Promise<void> {
  const db = await getDB();
  await db.delete("panelLayouts", runId);
}

// ─── Widget Content ─────────────────────────────────────

export async function saveWidget(tile: TileData): Promise<void> {
  const db = await getDB();
  await db.put("widgetContent", {
    tileId: tile.tileId,
    runId: tile.runId,
    type: tile.type,
    title: tile.title,
    content: tile.content,
    createdAt: tile.createdAt,
    updatedAt: tile.updatedAt,
  });
}

export async function getWidgetsByRun(runId: string): Promise<TileData[]> {
  const db = await getDB();
  const records = await db.getAllFromIndex("widgetContent", "by-run", runId);
  return records as TileData[];
}

export async function deleteWidget(tileId: string): Promise<void> {
  const db = await getDB();
  await db.delete("widgetContent", tileId);
}

export async function getActiveTaskCountByRun(runId: string): Promise<number> {
  const db = await getDB();
  const widgets = await db.getAllFromIndex("widgetContent", "by-run", runId);
  let count = 0;
  for (const w of widgets) {
    if (w.type !== "human_task") continue;
    try {
      const parsed = JSON.parse(w.content);
      const status = parsed.status ?? "pending";
      if (status === "pending" || status === "in_progress") count++;
    } catch {
      // Malformed JSON — count as pending
      count++;
    }
  }
  return count;
}

export async function getActiveTaskCountsByRuns(
  runIds: string[],
): Promise<Map<string, number>> {
  const results = new Map<string, number>();
  // Batch: open one transaction for all reads
  const db = await getDB();
  const tx = db.transaction("widgetContent", "readonly");
  const index = tx.store.index("by-run");

  for (const runId of runIds) {
    const widgets = await index.getAll(IDBKeyRange.only(runId));
    let count = 0;
    for (const w of widgets) {
      if (w.type !== "human_task") continue;
      try {
        const parsed = JSON.parse(w.content);
        const status = parsed.status ?? "pending";
        if (status === "pending" || status === "in_progress") count++;
      } catch {
        count++;
      }
    }
    if (count > 0) results.set(runId, count);
  }

  await tx.done;
  return results;
}

export async function deleteWidgetsByRun(runId: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("widgetContent", "readwrite");
  const index = tx.store.index("by-run");
  let cursor = await index.openCursor(IDBKeyRange.only(runId));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
}

// ─── Panel Views ────────────────────────────────────────

export async function saveView(view: ViewData): Promise<void> {
  const db = await getDB();
  await db.put("panelViews", {
    viewId: view.viewId,
    runId: view.runId,
    title: view.title,
    mode: view.mode,
    tileIds: view.tileIds,
    icon: view.icon,
    sortOrder: view.sortOrder,
    isSystem: view.isSystem,
    isHidden: view.isHidden,
    createdAt: view.createdAt,
    updatedAt: view.updatedAt,
  });
}

export async function getViewsByRun(runId: string): Promise<ViewData[]> {
  const db = await getDB();
  const records = await db.getAllFromIndex("panelViews", "by-run", runId);
  return (records as ViewData[]).sort((a, b) => a.sortOrder - b.sortOrder);
}

export async function deleteView(viewId: string): Promise<void> {
  const db = await getDB();
  await db.delete("panelViews", viewId);
}

export async function deleteViewsByRun(runId: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("panelViews", "readwrite");
  const index = tx.store.index("by-run");
  let cursor = await index.openCursor(IDBKeyRange.only(runId));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
}
